import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { isNil } from 'ramda';

import CashbackPresenter, {
  QUARANTINED,
} from '../../../presenters/CashbackPresenter';
import DatePresenter from '../../../presenters/DatePresenter';
import StateChangeModal from '../../../components/StateChangeModal';
import { StoreProvider, Dialog, ErrorFlash } from '../../../components';
import { deleteCashback, updateCashbackState } from '../../../mutations';

import Header from './components/Header';
import CashbackInfo from './components/CashbackInfo';

const CashbackShowView = (props) => {
  const {
    cashback,
    relay: { environment },
  } = props;

  const [cashbackState, setCashbackState] = useState(cashback);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const handleDeleteDialogOpen = () => setIsDialogOpen(true);
  const handleDeleteDialogClose = () => setIsDialogOpen(false);
  const [errors, setErrors] = useState(null);
  const [isEditCashbackStateDialogOpen, setIsEditCashbackStateDialogOpen] =
    useState(false);

  const warningText = `Are you sure you want to remove $${CashbackPresenter.amount(
    cashback
  )} FastCash for 
    ${CashbackPresenter.name(cashback)}?`;

  const handleDeleteConfirm = () => {
    const params = {
      cashbackId: CashbackPresenter.id(cashback),
      afterDate: DatePresenter.oneMonthAgo(),
      beforeDate: DatePresenter.today(),
      // Not required because of redirect
      byAuth0Id: '',
      bankAccountIdEq: '',
    };

    deleteCashback(environment, params, 'LISTS', {
      onCompleted: (response) => handleDeleteCashbackSuccess(response),
      onError: (error) => setErrors(error.message),
    });
  };

  const handleDeleteCashbackSuccess = (response) => {
    const { errorMessage, cashback } = response.deleteCashback;
    if (!isNil(errorMessage)) return setErrors(errorMessage);

    setIsDialogOpen(false);
    setCashbackState(cashback);
  };

  const handleEditCashbackStateDialogOpen = () =>
    setIsEditCashbackStateDialogOpen(true);

  const handleEditCashbackStateDialogClose = () =>
    setIsEditCashbackStateDialogOpen(false);

  const handleSubmit = (values) => {
    const { state } = values;
    const newValues =
      state === QUARANTINED ? values : { ...values, quarantinedReason: null };
    const params = {
      ...newValues,
      id: CashbackPresenter.id(cashbackState),
    };

    return updateCashbackState(environment, params, {
      onCompleted: (response) => handleSubmitSuccess(response),
    });
  };

  const handleSubmitSuccess = ({ updateCashbackState }) => {
    const { errors, errorMessage, cashback } = updateCashbackState;
    if (errors) return setErrors(errors);
    if (errorMessage) return setErrors(errorMessage);

    setCashbackState(cashback);
    handleEditCashbackStateDialogClose();
  };

  if (isNil(cashbackState)) {
    return <div>The cashback has been removed or does not exist</div>;
  }

  return (
    <div className="cashback">
      {!isNil(errors) && <ErrorFlash errors={errors} message={errors} />}

      <Dialog
        className="deletion"
        isOpen={isDialogOpen}
        message={warningText}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteDialogClose}
      />
      <StateChangeModal
        cashbackParams={cashbackState}
        onEditCashbackStateDialogClose={handleEditCashbackStateDialogClose}
        isEditDialogOpen={isEditCashbackStateDialogOpen}
        onSubmit={handleSubmit}
        errors={errors}
      />
      <Header
        cashback={cashbackState}
        onDeleteDialogOpen={handleDeleteDialogOpen}
        onEditCashbackStateDialogOpen={handleEditCashbackStateDialogOpen}
      />

      <CashbackInfo cashback={cashbackState} />
    </div>
  );
};

const CashbackShowContainer = createFragmentContainer(CashbackShowView, {
  cashback: graphql`
    fragment CashbackShow_cashback on Cashback {
      id
      date
      name
      rate
      amount
      source
      pended
      plaidTransactionId
      transactionAmount
      fastCashId
      state
      quarantinedReason
      availableStates
      userAuth0Id
    }
  `,
});

const CashbackShow = () => {
  return (
    <StoreProvider
      container={CashbackShowContainer}
      modern
      query={graphql`
        query CashbackShowQuery($cashbackId: ID!) {
          cashback: cashback(id: $cashbackId) {
            ...CashbackShow_cashback
          }
        }
      `}
    />
  );
};

export default CashbackShow;

CashbackShowView.propTypes = {
  cashback: CashbackPresenter.shape(),
  relay: PropTypes.shape().isRequired,
};

CashbackShowView.defaultProps = {
  cashback: null,
};
